import type { ReactNode } from 'react';
import {
  Dialog,
  type DialogProps,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Tooltip,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useGuideActive } from '@/lib/guides/engine';

const SlideUpTransition = forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ResponsiveDialogProps extends DialogProps {
  mobileTitle?: string;
  children: ReactNode;
}

export function ResponsiveDialog({
  mobileTitle,
  children,
  onClose,
  ...props
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile();
  const { t: tc } = useTranslation('common');
  const guideActive = useGuideActive();
  // A running guide must leave focus free so the user can type in Buddy
  // while the wizard dialog is open. Unchanged when no guide is active.
  const guideFocus = guideActive
    ? { disableEnforceFocus: true, disableRestoreFocus: true }
    : {};

  if (isMobile) {
    return (
      <Dialog
        fullScreen
        TransitionComponent={SlideUpTransition}
        onClose={onClose}
        {...props}
        {...guideFocus}
      >
        <AppBar sx={{ position: 'relative' }} elevation={0}>
          <Toolbar>
            {onClose && (
              <Tooltip title={tc('close')}>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={(e) => onClose(e, 'escapeKeyDown')}
                  aria-label={tc('close')}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
            {mobileTitle && (
              <Typography sx={{ ml: onClose ? 1 : 0, flex: 1 }} variant="h6">
                {mobileTitle}
              </Typography>
            )}
          </Toolbar>
        </AppBar>
        {children}
      </Dialog>
    );
  }

  return (
    <Dialog onClose={onClose} {...props} {...guideFocus}>
      {children}
    </Dialog>
  );
}
